<p-toast></p-toast>

<h2 class="page-title">
  {{ "i18n.domain.invoice.overview.title" | translate }}
</h2>

<div class="invoice-cards">
  <div class="stat-card stat-card--total">
    <i class="pi pi-file"></i>
    <div>
      <div class="stat-value">{{ overview.totalInvoices }}</div>
      <div class="stat-label">
        {{ "i18n.domain.invoice.cards.total-invoices" | translate }}
      </div>
    </div>
  </div>
  <div class="stat-card stat-card--pending">
    <i class="pi pi-clock"></i>
    <div>
      <div class="stat-value">{{ overview.pendingPayments }}</div>
      <div class="stat-label">
        {{ "i18n.domain.invoice.cards.pending-payments" | translate }}
      </div>
    </div>
  </div>
  <div class="stat-card stat-card--paid">
    <i class="pi pi-check-circle"></i>
    <div>
      <div class="stat-value">{{ overview.paidInvoices }}</div>
      <div class="stat-label">
        {{ "i18n.domain.invoice.cards.paid-invoices" | translate }}
      </div>
    </div>
  </div>
  <div class="stat-card stat-card--overdue">
    <i class="pi pi-exclamation-circle"></i>
    <div>
      <div class="stat-value">{{ overview.overdueInvoices }}</div>
      <div class="stat-label">
        {{ "i18n.domain.invoice.cards.overdue-invoices" | translate }}
      </div>
    </div>
  </div>
</div>

<div class="data-table">
  <p-toolbar>
    <ng-template pTemplate="left">
      <div class="filter-row">
        <p-dropdown
          [options]="statusOptions"
          [(ngModel)]="selectedStatus"
          optionLabel="label"
          optionValue="value"
          placeholder="{{
            'i18n.domain.invoice.filters.filter-by-status' | translate
          }}"
        ></p-dropdown>
        <p-calendar
          [(ngModel)]="dateRange"
          selectionMode="range"
          [readonlyInput]="true"
          placeholder="{{
            'i18n.domain.invoice.filters.date-range' | translate
          }}"
          dateFormat="dd M yy"
        ></p-calendar>
        <p-button
          label="{{ 'i18n.domain.invoice.filters.apply' | translate }}"
          icon="pi pi-search"
          (onClick)="applyFilters()"
        ></p-button>
        <p-button
          label="{{ 'i18n.domain.invoice.filters.clear' | translate }}"
          icon="pi pi-times"
          severity="secondary"
          [outlined]="true"
          (onClick)="clearFilters()"
        ></p-button>
      </div>
    </ng-template>

    <ng-template pTemplate="right">
      <app-search-field
        (searchTextChanged)="onSearchTextChanged($event)"
      ></app-search-field>
      <p-button
        label="Export"
        icon="pi pi-download"
        [outlined]="true"
        (onClick)="exportMenu.toggle($event)"
      ></p-button>
      <p-menu #exportMenu [model]="exportMenuItems" [popup]="true"></p-menu>
    </ng-template>
  </p-toolbar>

  <p-table
    [value]="overview.invoices"
    [loading]="loading"
    [lazy]="true"
    [totalRecords]="totalNoOfRecords"
    [rows]="take"
    [paginator]="true"
    [rowsPerPageOptions]="[10, 20, 50]"
    (onPage)="onPageChange($event)"
    [rowHover]="true"
    dataKey="id"
    styleClass="dt-grid"
    currentPageReportTemplate="{{
      'i18n.domain.invoice.table.showing' | translate
    }}"
    [showCurrentPageReport]="true"
  >
    <ng-template pTemplate="header">
      <tr>
        <th>{{ "i18n.domain.invoice.table.invoice-number" | translate }}</th>
        <th>{{ "i18n.domain.invoice.table.user" | translate }}</th>
        <th>{{ "i18n.domain.invoice.table.plan" | translate }}</th>
        <th>{{ "i18n.domain.invoice.table.billing-period" | translate }}</th>
        <th>{{ "i18n.domain.invoice.table.invoice-date" | translate }}</th>
        <th class="dt-num">
          {{ "i18n.domain.invoice.table.amount" | translate }}
        </th>
        <th class="dt-narrow">
          {{ "i18n.domain.invoice.table.status" | translate }}
        </th>
        <th>{{ "i18n.domain.invoice.table.payment-date" | translate }}</th>
        <th class="dt-actions-wide"></th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-invoice>
      <tr [class.overdue-row]="invoice.status === InvoiceStatus.Overdue">
        <td>{{ invoice.invoiceNumber }}</td>
        <td>{{ invoice.userName || "—" }}</td>
        <td>{{ invoice.subscriptionPlanName || "—" }}</td>
        <td>{{ formatBillingPeriod(invoice) }}</td>
        <td>{{ invoice.invoiceDate?.toFormat("dd MMM yyyy") }}</td>
        <td class="dt-num">₹{{ invoice.totalAmount | number: "1.2-2" }}</td>
        <td class="dt-narrow">
          <p-tag
            [value]="getStatusLabel(invoice.status)"
            [severity]="getStatusSeverity(invoice.status)"
          ></p-tag>
        </td>
        <td>
          {{
            invoice.paymentDate
              ? invoice.paymentDate.toFormat("dd MMM yyyy")
              : "—"
          }}
        </td>
        <td class="dt-actions-wide">
          <div class="grid-commands">
            <p-button
              icon="pi pi-eye"
              [rounded]="true"
              [outlined]="true"
              severity="info"
              pTooltip="{{
                'i18n.domain.invoice.actions.view-details' | translate
              }}"
              tooltipPosition="top"
              (onClick)="viewDetail(invoice)"
            ></p-button>
            <p-button
              *ngIf="canRecordPayment(invoice)"
              icon="pi pi-credit-card"
              [rounded]="true"
              [outlined]="true"
              severity="success"
              pTooltip="{{
                'i18n.domain.invoice.actions.record-payment' | translate
              }}"
              tooltipPosition="top"
              (onClick)="openPaymentDialog(invoice)"
            ></p-button>
            <p-button
              *ngIf="canCancel(invoice)"
              icon="pi pi-ban"
              [rounded]="true"
              [outlined]="true"
              severity="danger"
              pTooltip="{{
                'i18n.domain.invoice.actions.cancel-invoice' | translate
              }}"
              tooltipPosition="top"
              (onClick)="confirmCancel(invoice)"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="9">
          {{ "i18n.domain.invoice.overview.no-records" | translate }}
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>

<p-dialog
  [(visible)]="detailDialog"
  [style]="{ width: '750px', maxHeight: '92vh' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="p-fluid detail-dialog"
>
  <ng-template pTemplate="header">
    <div class="dialog-header">
      <span class="dialog-title">{{
        "i18n.domain.invoice.detail-dialog.title" | translate
      }}</span>
    </div>
  </ng-template>

  <ng-template pTemplate="content">
    <ng-container *ngIf="selectedInvoice">
      <div class="detail-section">
        <h4>
          {{
            "i18n.domain.invoice.detail-dialog.invoice-information" | translate
          }}
        </h4>
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.invoice-number" | translate
            }}</span>
            <span>{{ selectedInvoice.invoiceNumber }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.user" | translate
            }}</span>
            <span>{{ selectedInvoice.userName }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.plan" | translate
            }}</span>
            <span>{{ selectedInvoice.subscriptionPlanName }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.billing-period" | translate
            }}</span>
            <span>{{ formatBillingPeriod(selectedInvoice) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.invoice-date" | translate
            }}</span>
            <span>{{
              selectedInvoice.invoiceDate?.toFormat("dd MMM yyyy")
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.due-date" | translate
            }}</span>
            <span>{{
              selectedInvoice.dueDate?.toFormat("dd MMM yyyy") || "—"
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.status" | translate
            }}</span>
            <div style="margin-top: 0.15rem">
              <p-tag
                [value]="getStatusLabel(selectedInvoice.status)"
                [severity]="getStatusSeverity(selectedInvoice.status)"
              ></p-tag>
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.total-amount" | translate
            }}</span>
            <span class="detail-amount"
              >₹{{ selectedInvoice.totalAmount | number: "1.2-2" }}</span
            >
          </div>
        </div>
      </div>

      <div class="detail-section">
        <h4>
          {{ "i18n.domain.invoice.detail-dialog.line-items" | translate }}
        </h4>
        <div class="data-table data-table--sm">
          <p-table
            [value]="selectedInvoice.invoiceLines || []"
            styleClass="dt-grid"
          >
            <ng-template pTemplate="header">
              <tr>
                <th>
                  {{
                    "i18n.domain.invoice.detail-dialog.description" | translate
                  }}
                </th>
                <th class="dt-narrow dt-center">
                  {{ "i18n.domain.invoice.detail-dialog.qty" | translate }}
                </th>
                <th class="dt-num" style="width: 130px">
                  {{ "i18n.domain.invoice.detail-dialog.amount" | translate }}
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-line>
              <tr>
                <td>{{ line.description }}</td>
                <td class="dt-narrow dt-center">{{ line.quantity }}</td>
                <td class="dt-num">₹{{ line.amount | number: "1.2-2" }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3">
                  {{
                    "i18n.domain.invoice.detail-dialog.no-line-items"
                      | translate
                  }}
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr>
                <td
                  colspan="2"
                  style="
                    text-align: right;
                    font-weight: 700;
                    padding: 0 0.75rem;
                  "
                >
                  {{ "i18n.domain.invoice.detail-dialog.total" | translate }}
                </td>
                <td class="dt-num" style="font-weight: 700">
                  ₹{{ selectedInvoice.totalAmount | number: "1.2-2" }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </div>

      <div
        class="detail-section"
        *ngIf="selectedInvoice.status === InvoiceStatus.Paid"
      >
        <h4>
          {{
            "i18n.domain.invoice.detail-dialog.payment-information" | translate
          }}
        </h4>
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.payment-date" | translate
            }}</span>
            <span>{{
              selectedInvoice.paymentDate?.toFormat("dd MMM yyyy")
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.method" | translate
            }}</span>

            <span>{{
              getPaymentMethodLabel(selectedInvoice.paymentMethod)
            }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.reference" | translate
            }}</span>
            <span>{{ selectedInvoice.transactionReference || "—" }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{
              "i18n.domain.invoice.detail-dialog.notes" | translate
            }}</span>
            <span>{{ selectedInvoice.paymentNotes || "—" }}</span>
          </div>
        </div>
      </div>
    </ng-container>
  </ng-template>

  <ng-template pTemplate="footer">
    <p-button
      *ngIf="selectedInvoice && canRecordPayment(selectedInvoice)"
      label="{{ 'i18n.domain.invoice.actions.record-payment' | translate }}"
      icon="pi pi-credit-card"
      [text]="true"
      (onClick)="openPaymentDialog(selectedInvoice); detailDialog = false"
    ></p-button>
    <p-button
      label="{{ 'i18n.domain.invoice.detail-dialog.close' | translate }}"
      icon="pi pi-times"
      [text]="true"
      (onClick)="detailDialog = false"
    ></p-button>
  </ng-template>
</p-dialog>

<p-dialog
  [(visible)]="paymentDialog"
  [style]="{ width: '480px' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="p-fluid"
>
  <ng-template pTemplate="header">
    <div class="dialog-header">
      <span class="dialog-title">{{
        "i18n.domain.invoice.payment-dialog.title" | translate
      }}</span>
    </div>
  </ng-template>

  <ng-template pTemplate="content">
    <div *ngIf="paymentTargetInvoice">
      <div class="invoice-summary-bar">
        <span>{{ paymentTargetInvoice.invoiceNumber }}</span>
        <span class="invoice-amount"
          >₹{{ paymentTargetInvoice.totalAmount | number: "1.2-2" }}</span
        >
      </div>

      <form [formGroup]="paymentForm" class="p-fluid">
        <div class="field">
          <label for="paymentAmount" class="control-label required">{{
            "i18n.domain.invoice.payment-dialog.payment-amount" | translate
          }}</label>
          <p-inputNumber
            inputId="paymentAmount"
            formControlName="paymentAmount"
            mode="decimal"
            [minFractionDigits]="2"
          ></p-inputNumber>
        </div>
        <div class="field">
          <label for="paymentDate" class="control-label required">{{
            "i18n.domain.invoice.payment-dialog.payment-date" | translate
          }}</label>
          <p-calendar
            inputId="paymentDate"
            formControlName="paymentDate"
            dateFormat="dd M yy"
            [showIcon]="true"
            iconDisplay="input"
          ></p-calendar>
        </div>
        <div class="field">
          <label for="paymentMethod" class="control-label required">{{
            "i18n.domain.invoice.payment-dialog.payment-method" | translate
          }}</label>
          <p-dropdown
            inputId="paymentMethod"
            [options]="paymentMethodOptions"
            formControlName="paymentMethod"
            optionLabel="label"
            optionValue="value"
            placeholder="{{
              'i18n.domain.invoice.payment-dialog.select-method' | translate
            }}"
            [appendTo]="'body'"
          ></p-dropdown>
        </div>
        <div class="field">
          <label for="transactionReference">{{
            "i18n.domain.invoice.payment-dialog.transaction-reference"
              | translate
          }}</label>
          <input
            id="transactionReference"
            pInputText
            formControlName="transactionReference"
            placeholder="{{
              'i18n.domain.invoice.payment-dialog.transaction-reference-placeholder'
                | translate
            }}"
          />
        </div>
        <div class="field">
          <label for="notes">{{
            "i18n.domain.invoice.payment-dialog.notes" | translate
          }}</label>
          <textarea
            id="notes"
            pInputTextarea
            formControlName="notes"
            rows="3"
            placeholder="{{
              'i18n.domain.invoice.payment-dialog.notes-placeholder' | translate
            }}"
          ></textarea>
        </div>
      </form>
    </div>
  </ng-template>

  <ng-template pTemplate="footer">
    <p-button
      label="{{ 'i18n.domain.invoice.payment-dialog.cancel' | translate }}"
      icon="pi pi-times"
      [text]="true"
      (onClick)="paymentDialog = false"
    ></p-button>
    <p-button
      label="{{
        'i18n.domain.invoice.payment-dialog.save-payment' | translate
      }}"
      icon="pi pi-check"
      [text]="true"
      [loading]="savingPayment"
      [disabled]="!paymentForm?.valid"
      (onClick)="savePayment()"
    ></p-button>
  </ng-template>
</p-dialog>
